<header class="expense-panel__header">
  <h2 class="expense-panel__title">
    {{
      (isEditMode ? 'expenses.form.edit_title' : 'expenses.form.create_title')
        | translate
    }}
  </h2>

  <button
    type="button"
    class="expense-panel__close"
    [attr.aria-label]="'expenses.close' | translate"
    (click)="close()"
  >
    <tui-icon icon="tuiIconClose" [style.font-size.rem]="1.25"></tui-icon>
  </button>
</header>

<form class="expense-panel__body" [formGroup]="form" (ngSubmit)="submit()">
  <h3 class="expense-panel__section-title">
    {{ 'expenses.form.section_info' | translate }}
  </h3>

  <!--
    Servicio al que se imputa el gasto. La API exige `nodeId` para dar de alta un
    gasto y el diseño de Figma no contempla el campo. Se añade sólo en alta; sin
    él la llamada fallaría con 400.

    Se pide el número de pedido, no el nodo: el backend resuelve las dos formas y
    el pedido es lo que operaciones tiene a mano. El nombre del control y el de la
    clave del multipart siguen siendo `nodeId` porque así se llaman en el contrato.
  -->
  <div *ngIf="!isEditMode" class="expense-field">
    <label class="expense-field__label" for="expense-node">
      {{ 'expenses.form.node_id' | translate
      }}<span class="expense-field__required">*</span>
    </label>
    <input
      #firstField
      id="expense-node"
      class="expense-field__input"
      type="text"
      autocomplete="off"
      formControlName="nodeId"
      [placeholder]="'expenses.form.node_id_placeholder' | translate"
      (blur)="onNodeIdBlur()"
    />
    <p class="expense-field__help">
      {{ 'expenses.form.node_id_help' | translate }}
    </p>
    <p
      *ngIf="
        form.controls.nodeId.touched && form.controls.nodeId.hasError('required')
      "
      class="expense-field__error"
    >
      {{ 'expenses.form.required' | translate }}
    </p>
    <p
      *ngIf="
        form.controls.nodeId.touched && form.controls.nodeId.hasError('nodeId')
      "
      class="expense-field__error"
    >
      {{ 'expenses.form.node_id_invalid' | translate }}
    </p>
  </div>

  <!-- Conductor: buscador en alta, bloqueado en edición -->
  <div class="expense-field">
    <label class="expense-field__label" for="expense-driver">
      {{ 'expenses.form.driver' | translate
      }}<span *ngIf="!isEditMode" class="expense-field__required">*</span>
    </label>

    <input
      *ngIf="isEditMode; else driverSelect"
      id="expense-driver"
      class="expense-field__input"
      type="text"
      readonly
      [value]="expense?.driverName ?? ''"
    />

    <ng-template #driverSelect>
      <ng-select
        id="expense-driver"
        class="expense-field__ng-select"
        formControlName="driverId"
        bindLabel="fullName"
        bindValue="driverId"
        [items]="drivers"
        [clearable]="true"
        [loading]="isLoadingDrivers"
        [loadingText]="'Loading' | translate"
        [placeholder]="
          (isLoadingDrivers
            ? 'expenses.form.driver_loading'
            : 'expenses.form.driver_placeholder'
          ) | translate
        "
      ></ng-select>
    </ng-template>

    <p
      *ngIf="
        !isEditMode &&
        form.controls.driverId.touched &&
        form.controls.driverId.hasError('required')
      "
      class="expense-field__error"
    >
      {{ 'expenses.form.required' | translate }}
    </p>

    <p *ngIf="hasDriversError" class="expense-field__error">
      {{ 'expenses.alerts.error_drivers' | translate }}
      <button type="button" class="expense-field__retry" (click)="retryDrivers()">
        {{ 'expenses.retry' | translate }}
      </button>
    </p>
  </div>

  <!-- Order Hash: sólo en edición, informativo -->
  <div *ngIf="isEditMode" class="expense-field">
    <label class="expense-field__label" for="expense-order-hash">
      {{ 'expenses.form.order_hash' | translate }}
    </label>
    <input
      id="expense-order-hash"
      class="expense-field__input"
      type="text"
      readonly
      [value]="expense?.orderHash ?? ''"
    />
  </div>

  <!-- Matrícula: sólo en alta -->
  <div *ngIf="!isEditMode" class="expense-field">
    <label class="expense-field__label" for="expense-plate">
      {{ 'expenses.form.license_plate' | translate
      }}<span class="expense-field__required">*</span>
    </label>
    <input
      id="expense-plate"
      class="expense-field__input"
      type="text"
      autocomplete="off"
      formControlName="licensePlate"
      [placeholder]="'expenses.form.license_plate_placeholder' | translate"
      (blur)="onLicensePlateBlur()"
    />
    <p
      *ngIf="
        form.controls.licensePlate.touched &&
        form.controls.licensePlate.hasError('required')
      "
      class="expense-field__error"
    >
      {{ 'expenses.form.required' | translate }}
    </p>
    <p
      *ngIf="
        form.controls.licensePlate.touched &&
        form.controls.licensePlate.hasError('licensePlate')
      "
      class="expense-field__error"
    >
      {{ 'expenses.form.plate_invalid' | translate }}
    </p>
  </div>

  <!-- Categoría -->
  <div class="expense-field">
    <label class="expense-field__label" for="expense-category">
      {{ 'expenses.form.category' | translate
      }}<span class="expense-field__required">*</span>
    </label>
    <select
      #firstField
      id="expense-category"
      class="form-select expense-field__input"
      formControlName="category"
    >
      <option *ngFor="let category of categories" [ngValue]="category">
        {{ category | expenseCategoryLabel }}
      </option>
    </select>
  </div>

  <!-- Descripción -->
  <div class="expense-field">
    <label class="expense-field__label" for="expense-description">
      {{ 'expenses.form.description' | translate
      }}<span class="expense-field__required">*</span>
    </label>
    <textarea
      id="expense-description"
      class="expense-field__textarea"
      rows="4"
      formControlName="description"
      [attr.maxlength]="descriptionMaxLength"
      [placeholder]="'expenses.form.description_placeholder' | translate"
    ></textarea>
    <span
      class="expense-field__counter"
      [class.expense-field__counter--limit]="
        descriptionLength >= descriptionMaxLength
      "
      >{{ descriptionLength }}/{{ descriptionMaxLength }}</span
    >
    <p
      *ngIf="
        form.controls.description.touched &&
        form.controls.description.hasError('required')
      "
      class="expense-field__error"
    >
      {{ 'expenses.form.required' | translate }}
    </p>
  </div>

  <!-- Importe -->
  <div class="expense-field">
    <label class="expense-field__label" for="expense-amount">
      {{ 'expenses.form.amount' | translate
      }}<span class="expense-field__required">*</span>
    </label>
    <div class="expense-field__amount">
      <input
        id="expense-amount"
        class="expense-field__input expense-field__input--amount"
        type="text"
        inputmode="decimal"
        autocomplete="off"
        formControlName="amount"
        placeholder="0,00"
        (focus)="onAmountFocus($event)"
        (blur)="onAmountBlur()"
      />
    </div>
    <p
      *ngIf="
        form.controls.amount.touched && form.controls.amount.hasError('required')
      "
      class="expense-field__error"
    >
      {{ 'expenses.form.required' | translate }}
    </p>
    <p
      *ngIf="
        form.controls.amount.touched &&
        form.controls.amount.hasError('amountFormat')
      "
      class="expense-field__error"
    >
      {{ 'expenses.form.amount_invalid' | translate }}
    </p>
    <p
      *ngIf="
        form.controls.amount.touched && form.controls.amount.hasError('amountMin')
      "
      class="expense-field__error"
    >
      {{ 'expenses.form.amount_min' | translate }}
    </p>
  </div>

  <!-- Estado -->
  <div class="expense-field">
    <label class="expense-field__label" for="expense-status">
      {{ 'expenses.form.status' | translate
      }}<span class="expense-field__required">*</span>
    </label>
    <select
      id="expense-status"
      class="form-select expense-field__input"
      formControlName="status"
    >
      <option *ngFor="let status of statuses" [ngValue]="status">
        {{ (status | expenseStatusBadge)?.labelKey ?? '' | translate }}
      </option>
    </select>
  </div>

  <!-- Motivo del rechazo: sólo aplica al rechazar -->
  <!--
    Motivo del catálogo, sólo al devolver: el rechazo definitivo no lo tiene y se
    explica escribiendo. Es el mismo catálogo que ofrece el modal de revisión.
  -->
  <div *ngIf="needsReturnReason" class="expense-field expense-field--danger">
    <label class="expense-field__label" for="expense-reason-code">
      {{ 'expenses.review_modal.return_reason' | translate }}
      <span class="expense-field__required">*</span>
    </label>
    <select
      id="expense-reason-code"
      class="form-select expense-field__input"
      formControlName="reasonCode"
    >
      <!-- Sin preselección: devolver sin mirar mandaría un motivo al azar. -->
      <option [ngValue]="null" disabled>
        {{ 'expenses.review_modal.return_reason_placeholder' | translate }}
      </option>
      <option *ngFor="let option of returnReasons" [ngValue]="option">
        {{ returnReasonLabels[option] | translate }}
      </option>
    </select>
    <p
      *ngIf="
        form.controls.reasonCode.touched &&
        form.controls.reasonCode.hasError('required')
      "
      class="expense-field__error"
    >
      {{ 'expenses.review_modal.return_reason_required' | translate }}
    </p>
  </div>

  <div *ngIf="needsReason" class="expense-field expense-field--danger">
    <label class="expense-field__label" for="expense-reason">
      {{
        (needsReturnReason
          ? 'expenses.review_modal.reason_detail'
          : 'expenses.form.rejection_reason'
        ) | translate
      }}
      <!-- Sigue al validador: al devolver el texto sólo amplía y no es obligatorio. -->
      <span *ngIf="isReasonRequired" class="expense-field__required">*</span>
    </label>
    <textarea
      id="expense-reason"
      class="expense-field__textarea"
      rows="3"
      formControlName="reason"
      [attr.maxlength]="reasonMaxLength"
      [placeholder]="reasonPlaceholderKey | translate"
    ></textarea>
    <span
      class="expense-field__counter"
      [class.expense-field__counter--limit]="reasonLength >= reasonMaxLength"
      >{{ reasonLength }}/{{ reasonMaxLength }}</span
    >
    <p
      *ngIf="
        form.controls.reason.touched &&
        form.controls.reason.hasError('required')
      "
      class="expense-field__error"
    >
      {{ 'expenses.form.required' | translate }}
    </p>
  </div>

  <!-- Comprobante -->
  <div class="expense-field">
    <span class="expense-field__label">{{
      'expenses.form.receipt' | translate
    }}</span>

    <div *ngIf="hasReceipt; else uploadZone" class="expense-receipt-preview">
      <button
        type="button"
        class="expense-receipt-preview__remove"
        [attr.aria-label]="'expenses.form.receipt_remove' | translate"
        (click)="removeReceipt()"
      >
        <tui-icon icon="tuiIconClose" [style.font-size.rem]="0.875"></tui-icon>
      </button>

      <img
        *ngIf="receiptPreviewUrl; else fileNameOnly"
        class="expense-receipt-preview__image"
        [src]="receiptPreviewUrl"
        [alt]="'expenses.receipt_attached' | translate"
      />

      <ng-template #fileNameOnly>
        <span class="expense-receipt-preview__file">
          <tui-icon icon="tuiIconFileText" [style.font-size.rem]="1.5"></tui-icon>
          {{ newReceiptFileName }}
        </span>
      </ng-template>
    </div>

    <ng-template #uploadZone>
      <label class="expense-upload">
        <input
          class="expense-upload__input"
          type="file"
          [accept]="acceptAttribute"
          (change)="onFileSelected($event)"
        />
        <tui-icon icon="tuiIconUpload" [style.font-size.rem]="1.125"></tui-icon>
        <span>{{ 'expenses.form.receipt_upload' | translate }}</span>
        <span class="expense-upload__hint">
          {{
            'expenses.form.receipt_hint' | translate : { size: maxSizeLabel }
          }}
        </span>
      </label>
    </ng-template>

    <p *ngIf="receiptError" class="expense-field__error">
      {{ receiptError | translate : { size: maxSizeLabel } }}
    </p>
  </div>
</form>

<footer
  class="expense-panel__footer"
  [class.expense-panel__footer--confirm]="isConfirmingDiscard"
>
  <!--
    El guardado ocurre con el panel abierto: si la API falla, el error se ve aquí
    y lo escrito sigue en pantalla.
  -->
  <p *ngIf="saveError" class="expense-panel__save-error" role="alert">
    <tui-icon icon="tuiIconAlertCircle" [style.font-size.rem]="1"></tui-icon>
    <span>{{ saveError | translate }}</span>
  </p>

  <ng-container *ngIf="!isConfirmingDiscard; else discardConfirmation">
    <button
      type="button"
      class="expense-panel__button expense-panel__button--secondary"
      [disabled]="isSaving"
      (click)="requestDiscard()"
    >
      {{ 'expenses.form.discard' | translate }}
    </button>

    <button
      type="button"
      class="expense-panel__button expense-panel__button--primary"
      [disabled]="isSaving"
      (click)="submit()"
    >
      <!-- El pie está fuera del <form>, así que Enter se gestiona en el propio
           formulario con (ngSubmit); aquí basta el clic. -->
      <tui-icon
        *ngIf="isSaving"
        icon="tuiIconLoader"
        [style.font-size.rem]="1"
      ></tui-icon>
      {{
        (isEditMode
          ? 'expenses.form.submit_edit'
          : 'expenses.form.submit_create'
        ) | translate
      }}
    </button>
  </ng-container>

  <ng-template #discardConfirmation>
    <p class="expense-discard__message">
      {{ 'expenses.form.unsaved_changes' | translate }}
    </p>
    <div class="expense-discard__actions">
      <button
        type="button"
        class="expense-panel__button expense-panel__button--secondary"
        (click)="cancelDiscard()"
      >
        {{ 'expenses.form.keep_editing' | translate }}
      </button>
      <button
        type="button"
        class="expense-panel__button expense-panel__button--danger"
        (click)="confirmDiscard()"
      >
        {{ 'expenses.form.discard_confirm' | translate }}
      </button>
    </div>
  </ng-template>
</footer>
